import type { Cycle } from "../components/frozen-storage-data";

/**
 * Stable per-item retry identity for specimen batches (#2431).
 *
 * Retry idempotency used to key items by `cycleIndex:specimenIndex`. Both
 * halves are unstable while the intake form stays open: removing or
 * reordering a sibling cycle card shifts every cycleIndex, and removing or
 * reordering individual embryo rows shifts specimenIndex. A shifted lookup
 * hands a NEW row the client-item key of an already-committed row, and the
 * server's per-item idempotency then silently drops that row instead of
 * creating it — clinical data loss on retry.
 *
 * Identities here are built from the draft rows' own stable ids:
 * - every cycle card carries a stable form id (`cycle.id`);
 * - individual embryo entries carry their own stable form-row id
 *   (`entry.id`), so reorder/removal cannot re-map them;
 * - counted rows (oocytes, sperm, group-mode embryos) are fungible WITHIN
 *   their cycle — identical interchangeable bodies — so a positional suffix
 *   under the stable cycle id is semantically correct for them.
 */
export function specimenItemIdentity(
  cycle: Pick<Cycle, "id" | "type" | "embryoEntryMode"> & {
    embryo: Pick<Cycle["embryo"], "individualEntries">;
  },
  specimenIndex: number,
): string {
  if (cycle.type === "embryos" && cycle.embryoEntryMode === "individual") {
    const entry = cycle.embryo.individualEntries[specimenIndex];
    if (entry) return `${cycle.id}:entry:${entry.id}`;
  }
  return `${cycle.id}:${cycle.type}:${specimenIndex}`;
}

/** Stable identity for a sperm cycle's shared collection sample. */
export function semenSampleIdentity(cycle: Pick<Cycle, "id">): string {
  return `${cycle.id}:sample`;
}
